import * as THREE from 'three';
import { makeSoldier, loadoutFor } from '../ai/Enemy.js';
import { paletteFor } from '../gameplay/operators.js';

/**
 * A soldier on a turntable, in a canvas.
 *
 * Two screens need to show a character model outside the game: the operator
 * picker (before you spawn, when there is no world at all) and the end-of-match
 * awards (during a match, when there is a world but it is not what you are
 * looking at). One stage serves both.
 *
 * It runs its OWN renderer rather than borrowing the game's. The alternative —
 * a second camera into the live scene — would need somewhere in the level to
 * put a mannequin, correct lighting wherever that happened to be, and a way to
 * stop bots walking through the shot. A 200-line standalone stage is cheaper
 * than any of that, and it works before a level exists.
 *
 * ## The pose
 *
 * `Enemy` drives its arms entirely from weapon IK (`_solveWeapon`), which needs
 * a target, a world and a per-frame update. None of those exist here, so the
 * rifle is hidden and the arms are posed by hand into a relaxed stand. A
 * mannequin holding an invisible rifle in a T-pose would be worse than a
 * mannequin holding nothing.
 */
export class OperatorStage {
  /**
   * @param {{width?:number, height?:number, dpr?:number}} opts
   */
  constructor({ width = 420, height = 620, dpr = Math.min(2, window.devicePixelRatio || 1) } = {}) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    // Matched to Engine. A model graded differently here than in the game would
    // mean the operator you picked is not the one that walks out of spawn.
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.canvas = this.renderer.domElement;
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.display = 'block';

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(30, width / height, 0.1, 40);
    /*
     * Framed head-to-boot with room to spare, on a 2:3 canvas.
     *
     * At 30 degrees the vertical field is 2*d*tan(15 deg), so the distance is
     * not a taste call: a 1.85 m soldier plus headroom needs about 2.4 m of
     * frame, which is a little over four metres back. Three metres — the first
     * guess — showed 1.8 m and cut off both the helmet and the boots.
     */
    this.camera.position.set(0, 0.86, 4.0);
    this.camera.lookAt(0, 0.98, 0);

    /**
     * Three lights, and each does one job: a warm key from front-left carries
     * the form, a cool fill from the right keeps the shadow side readable
     * rather than black, and a hard rim from behind separates a near-black
     * defender from a near-black background — which is the whole reason the
     * defenders' silhouette is legible on this screen at all.
     *
     * Intensities are low for a three-light rig because ACES is doing the
     * grading. The first pass ran the key at 2.6 and the rim at 3.2, and the
     * boots — mid-grey moulded rubber — came out looking like white trainers.
     */
    const key = new THREE.DirectionalLight(0xfff0dd, 1.75);
    key.position.set(-2.2, 3.4, 3.0);
    const fill = new THREE.DirectionalLight(0x8fb4e0, 0.5);
    fill.position.set(3.0, 1.4, 1.6);
    const rim = new THREE.DirectionalLight(0xffc98a, 1.7);
    rim.position.set(0.9, 2.0, -3.2);
    this.scene.add(key, fill, rim, new THREE.HemisphereLight(0x5a6b80, 0x1a1a18, 0.40));

    /*
     * A painted blob under the boots, not a real shadow.
     *
     * A shadow-mapped disc was the first attempt and it produced a hard black
     * rectangle thrown off to one side — the key light is a raking three-quarter
     * light, which is right for the figure and wrong for the ground, and the
     * shadow camera's own bounds cut the disc off square. There is no set here
     * to shade, so nothing is gained by solving that properly. A soft radial
     * gradient grounds the figure, costs one quad, and cannot be cut off.
     */
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const g2 = c.getContext('2d');
    const grad = g2.createRadialGradient(64, 64, 0, 64, 64, 64);
    grad.addColorStop(0, 'rgba(0,0,0,0.72)');
    grad.addColorStop(0.45, 'rgba(0,0,0,0.34)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g2.fillStyle = grad;
    g2.fillRect(0, 0, 128, 128);
    const blobTex = new THREE.CanvasTexture(c);
    blobTex.colorSpace = THREE.SRGBColorSpace;
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(1.5, 1.0).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ map: blobTex, transparent: true, depthWrite: false })
    );
    floor.position.y = 0.005;
    this.scene.add(floor);

    this.pivot = new THREE.Group();
    this.scene.add(this.pivot);

    /**
     * `makeSoldier` builds the rig facing -Z (it flips a Y-up, +Z-forward
     * authoring space onto the engine's -Z-forward convention), so a camera
     * sitting on +Z looks at the back of everyone's head. Half a turn here
     * makes spin 0 mean "facing the player", which is what every caller
     * assumes.
     */
    this.baseYaw = Math.PI;

    this.current = null;
    this._t = 0;
    this._cache = new Map();
    this._raf = 0;
    this._spin = 0;
    this._drag = null;
    this._running = false;
  }

  /** Swap in an operator. Models are cached, so flicking through is free. */
  show(operator, { ffa = false } = {}) {
    const key = `${operator.id}:${ffa ? 's' : 't'}`;
    if (this.current?.key === key) return;
    if (this.current) this.current.group.visible = false;

    let entry = this._cache.get(key);
    if (!entry) {
      const lo = loadoutFor(0, operator.side, { operator, ffa });
      const parts = makeSoldier(lo);
      // No IK here, so no rifle. See the class comment.
      parts.rifle.visible = false;
      for (const m of parts.meshes) { m.castShadow = false; m.receiveShadow = false; }
      poseAtEase(parts.bones);
      parts.root.position.y = 0;
      entry = { key, group: parts.root, parts, palette: paletteFor(operator, ffa) };
      this._cache.set(key, entry);
      this.pivot.add(parts.root);
    }
    entry.group.visible = true;
    this.current = entry;
  }

  /** Drag-to-turn, so a player can look at the back of the kit. */
  enableDrag(el = this.canvas) {
    el.style.cursor = 'grab';
    const down = (e) => {
      this._drag = { x: e.clientX, spin: this._spin };
      el.style.cursor = 'grabbing';
      el.setPointerCapture?.(e.pointerId);
    };
    const move = (e) => {
      if (!this._drag) return;
      this._spin = this._drag.spin + (e.clientX - this._drag.x) * 0.008;
      this._idleSpin = 0;
    };
    const up = () => { this._drag = null; el.style.cursor = 'grab'; };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }

  start() {
    if (this._running) return;
    this._running = true;
    let last = performance.now();
    const tick = (now) => {
      if (!this._running) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      this._t += dt;
      // Slow drift so the model is never a still image, plus a breath so it is
      // never a shop dummy either.
      if (!this._drag) this._spin += dt * 0.22;
      this.pivot.rotation.y = this.baseYaw + this._spin;
      if (this.current) {
        const b = this.current.parts.bones;
        const breath = Math.sin(this._t * 1.15) * 0.012;
        b.chest.rotation.x = -0.04 + breath;
        b.spine.rotation.x = 0.03 - breath * 0.5;
        b.head.rotation.y = Math.sin(this._t * 0.31) * 0.10;
      }
      this.renderer.render(this.scene, this.camera);
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  stop() { this._running = false; cancelAnimationFrame(this._raf); }

  resize(w, h) {
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  /** Give the GL context back. A stage left alive costs a context forever. */
  dispose() {
    this.stop();
    for (const e of this._cache.values()) {
      for (const m of e.parts.meshes) m.geometry.dispose();
      for (const m of Object.values(e.parts.mats)) m.dispose();
    }
    this._cache.clear();
    this.renderer.dispose();
    this.renderer.forceContextLoss?.();
  }
}

/**
 * A relaxed stand: weight even, arms hanging with a little clearance from the
 * plate carrier, elbows softly bent, feet at shoulder width.
 *
 * Written against the bone conventions in Enemy.js — every limb bone points
 * down its parent's -Y at rest, so Z rotates a limb away from the body and X
 * swings it forward.
 */
function poseAtEase(b) {
  b.hips.rotation.set(0, 0, 0);
  b.spine.rotation.set(0.03, 0, 0);
  b.chest.rotation.set(-0.04, 0, 0);
  b.neck.rotation.set(0.02, 0, 0);

  b.clavL.rotation.set(0, 0, -0.06);
  b.clavR.rotation.set(0, 0, 0.06);
  /*
   * Out from the ribs, then a forearm bend, so the hands hang clear of the
   * thighs instead of being pinned to the seams.
   *
   * The Z SIGN here was backwards and it is why the picker looked like it had
   * no hands. An arm bone points down its own -Y, so a +Z rotation carries the
   * hand toward +X. shL lives at +X, so shL needs a POSITIVE Z to abduct;
   * -0.13 on the shoulder plus -0.06 on the elbow swung the left arm 11 deg
   * ACROSS the body, and over a 550 mm arm that is 100 mm of travel — measured,
   * the glove ended up at x -0.07, which is inside the pelvis. Both hands were
   * buried in the hips, in the one screen where the model is all there is to
   * look at.
   *
   * Small numbers deliberately. The first pass used a 0.30 rad elbow, which
   * from the front reads as both forearms held out horizontally like a waiter —
   * the arm points down its parent's -Y, so an X rotation swings the whole
   * forearm forward rather than "bending it a little".
   */
  b.shL.rotation.set(0.04, 0, 0.15);
  b.shR.rotation.set(0.04, 0, -0.15);
  b.elL.rotation.set(0.12, 0, 0.05);
  b.elR.rotation.set(0.12, 0, -0.05);
  b.wrL.rotation.set(0.06, 0, 0);
  b.wrR.rotation.set(0.06, 0, 0);

  b.hipL.rotation.set(0, 0, 0.05);
  b.hipR.rotation.set(0, 0, -0.05);
  b.knL.rotation.set(0.06, 0, 0);
  b.knR.rotation.set(0.06, 0, 0);
  b.anL.rotation.set(-0.03, 0, 0);
  b.anR.rotation.set(-0.03, 0, 0);
}
